import { useEffect, useRef } from "react";

// Subtle animated layer of tiny drifting "digital" objects (dots, rings, squares)
// Sits fixed behind all content. Intentionally understated.
export const DigitalBackground = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let objects = [];

    const build = () => {
      const count = Math.min(56, Math.round((w * h) / 42000));
      objects = Array.from({ length: count }, () => {
        const kind = Math.random();
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.12,
          vy: -(0.05 + Math.random() * 0.18),
          size: 1.5 + Math.random() * 3.5,
          type: kind < 0.55 ? "dot" : kind < 0.8 ? "ring" : "square",
          baseAlpha: 0.12 + Math.random() * 0.25,
          phase: Math.random() * Math.PI * 2,
          tw: 0.4 + Math.random() * 0.9,
        };
      });
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = (o, t) => {
      const alpha = o.baseAlpha * (0.6 + 0.4 * Math.sin(t * o.tw + o.phase));
      ctx.strokeStyle = `rgba(0, 242, 254, ${alpha})`;
      ctx.fillStyle = `rgba(79, 172, 254, ${alpha})`;
      if (o.type === "dot") {
        ctx.beginPath();
        ctx.arc(o.x, o.y, o.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
      } else if (o.type === "ring") {
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(o.x, o.y, o.size, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.lineWidth = 1;
        const s = o.size;
        ctx.strokeRect(o.x - s / 2, o.y - s / 2, s, s);
      }
    };

    let raf;
    let last = performance.now();
    const tick = (now) => {
      const t = now / 1000;
      ctx.clearRect(0, 0, w, h);
      for (const o of objects) {
        o.x += o.vx;
        o.y += o.vy;
        if (o.y < -10) o.y = h + 10;
        if (o.x < -10) o.x = w + 10;
        if (o.x > w + 10) o.x = -10;
        draw(o, t);
      }
      last = now;
      raf = requestAnimationFrame(tick);
    };

    if (reduce) {
      // static single paint
      ctx.clearRect(0, 0, w, h);
      objects.forEach((o) => draw(o, 0));
    } else {
      raf = requestAnimationFrame(tick);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden
      className="fixed inset-0 z-0 pointer-events-none"
      data-testid="digital-background"
    />
  );
};
